import React, { useCallback, useEffect, useState } from 'react';
import Radio from '../RadioField/RadioField';
import Label from '../Label/Label';
import InputContainer from '../InputContainer/InputContainer';
import { BaseInputProps } from '../shared-types';
import useEvents from '../events/useEvents';
import { RadioGroupEvent, OnChangeEvent } from '../events/EventProvider';
import { getGetIdFn } from '../utils';

const getLabelId = getGetIdFn('Label');

export type Props = Omit<BaseInputProps, 'onChange'> & {
  onChange: (value: string) => void;
  options: { label: string; value: string }[];
  value?: string;
  label?: string;
  eventId?: string;
  'aria-required'?: boolean;
};

const RadioGroup: React.FC<Props> = ({
  onChange,
  options,
  styleInputContainer,
  value,
  label,
  eventId,
  'aria-required': ariaRequired,
}) => {
  const labelId = getLabelId();
  const [selectedValue, setSelectedValue] = useState(value);

  const triggerEvent = useEvents<OnChangeEvent<RadioGroupEvent>>(
    eventId,
    'RadioGroup'
  );

  const handleChange = useCallback(
    (newValue: string) => {
      if (selectedValue === newValue) {
        return;
      }
      setSelectedValue(newValue);
      onChange(newValue);
      triggerEvent({ value: newValue });
    },
    [onChange, selectedValue, triggerEvent]
  );

  useEffect(() => {
    if (!value) {
      return;
    }
    setSelectedValue(value);
  }, [value]);
  return (
    <InputContainer
      style={styleInputContainer}
      aria-labelledby={labelId}
      aria-required={ariaRequired}
    >
      <Label label={label} id={labelId} />
      <div data-testid="RadioGroup" role="radiogroup">
        {options.map((d, i) => (
          <Radio
            checked={selectedValue === d.value}
            label={d.label}
            aria-label={d.label}
            value={d.value}
            key={`${d.value}-${i.toString()}`}
            onChange={handleChange}
          />
        ))}
      </div>
    </InputContainer>
  );
};

export default RadioGroup;
